import { LoyaltyPreset, PointsExpirationPolicy } from "../../../types/loyalty";
import { formatCurrency } from "../../../utils/cashDrawerUtils";

/** Default earn ratio: 10 points per $1 (services and products alike).
 *  Pairs with the 500 pts = $5 redemption default (~1% cashback). */
export const EARN_POINTS = 10;
export const EARN_PER_AMOUNT = 1;

/** points_expiration_days used with the `rolling` policy. */
export const ROLLING_EXPIRATION_DAYS = 365;

/** Suggested redemption default: 500 points = $5 off. */
export const DEFAULT_REDEEM_POINTS_COST = 500;
export const DEFAULT_REDEEM_CASH_VALUE = 5;

/** The single redemption tier the salon offers (one reward row).
 *  `uuid` is set only when loaded from an existing backend reward. */
export interface RedemptionIncrement {
  uuid?: string;
  /** Dollar amount the reward is worth (LoyaltyReward.cash_value). */
  cashValue: number | string;
  /** Points required to redeem it (LoyaltyReward.points_cost). */
  pointsCost: number | string;
}

/** Split per-type earn rates + a single redemption increment. Publish writes
 *  one spend rule per applicable type and ONE reward (points_cost + cash_value
 *  driven by the increment). */
export interface ValueRewardsDraft {
  /** Points earned per $1 spent on services (service_spend rule points). */
  servicesRate: number | string;
  /** Points earned per $1 spent on products (retail_spend rule points). */
  productsRate: number | string;
  /** The single reward the wizard/settings publishes. */
  redemptionIncrement: RedemptionIncrement;
}

export interface ActionRewardsDraft {
  rebookingEnabled: boolean;
  rebookingPoints: number | string;
  visitsEnabled: boolean;
  visitsPoints: number | string;
  /** nth_visit interval (award every N visits; N > 1 keeps this distinct
   *  from the First appointment card, which publishes interval_count 1). */
  visitsInterval: number | string;
  /** First appointment card — publishes a capped nth_visit rule
   *  (interval_count 1, max_awards_per_client 1). */
  firstAppointmentEnabled: boolean;
  firstAppointmentPoints: number | string;
}

/** Every enabled card must have a usable positive-integer point value
 *  (and Visits needs a valid interval). */
export function isActionRewardsValid(a: ActionRewardsDraft): boolean {
  if (a.rebookingEnabled && !isValidEarnRate(a.rebookingPoints)) return false;
  if (a.visitsEnabled) {
    if (!isValidEarnRate(a.visitsPoints)) return false;
    if (toNumber(a.visitsInterval) < 1) return false;
  }
  if (a.firstAppointmentEnabled && !isValidEarnRate(a.firstAppointmentPoints))
    return false;
  return true;
}

export interface PersonalizeDraft {
  preset: LoyaltyPreset;
  expirationPolicy: PointsExpirationPolicy;
  /** Days until expiration when `expirationPolicy` is `rolling`; ignored
   *  otherwise. Wizard/Settings default to `ROLLING_EXPIRATION_DAYS`. */
  expirationDays: number | string;
  /** Hard-coded false in the current UI (Aug-25 punch list removed the
   *  question); still shipped on the settings payload. */
  deductFromCommission: boolean;
}

export interface LoyaltyWizardDraft {
  personalize: PersonalizeDraft;
  valueRewards: ValueRewardsDraft;
  actionRewards: ActionRewardsDraft;
}

export function toNumber(value: number | string, fallback = 0): number {
  const parsed = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** True when the value is a usable positive integer (earn or redeem rate). */
export function isValidEarnRate(value: number | string): boolean {
  const rate = toNumber(value);
  return Number.isInteger(rate) && rate > 0;
}

/** Negative/fractional numeric input → non-negative integer
 *  (clampBehavior is "none"; normalize here). */
export function normalizeRate(raw: number | string): number | string {
  if (typeof raw === "number") return Math.max(0, Math.floor(raw));
  return raw;
}

export function presetShowsServices(preset: LoyaltyPreset): boolean {
  return preset !== "retail";
}

export function presetShowsProducts(preset: LoyaltyPreset): boolean {
  return preset !== "service";
}

/** Every rate the preset makes visible must be a positive integer. */
export function isValueRewardsValid(
  value: ValueRewardsDraft,
  preset: LoyaltyPreset,
): boolean {
  if (!isValidRedemptionIncrement(value.redemptionIncrement)) return false;
  if (presetShowsServices(preset) && !isValidEarnRate(value.servicesRate))
    return false;
  if (presetShowsProducts(preset) && !isValidEarnRate(value.productsRate))
    return false;
  return true;
}

/** Positive integer points_cost. */
export function isValidPointsCost(value: number | string): boolean {
  const n = toNumber(value);
  return Number.isInteger(n) && n > 0;
}

/** Positive numeric cash_value (decimals allowed for future flexibility). */
export function isValidCashValue(value: number | string): boolean {
  const n = toNumber(value);
  return Number.isFinite(n) && n > 0;
}

export function isValidRedemptionIncrement(row: RedemptionIncrement): boolean {
  return isValidPointsCost(row.pointsCost) && isValidCashValue(row.cashValue);
}

/** Auto-generated name for a redemption reward row. */
export function redeemRewardName(
  pointsCost: number | string,
  cashValue: number | string,
): string {
  const points = toNumber(pointsCost);
  return `Redeem ${points.toLocaleString("en-US")} points for ${formatCurrency(toNumber(cashValue))} off`;
}

export function defaultWizardDraft(): LoyaltyWizardDraft {
  return {
    personalize: {
      preset: "both",
      expirationPolicy: "none",
      expirationDays: ROLLING_EXPIRATION_DAYS,
      deductFromCommission: false,
    },
    valueRewards: {
      servicesRate: EARN_POINTS,
      productsRate: EARN_POINTS,
      // Single prefilled increment ($5 = 500pts) — one reward per program.
      redemptionIncrement: {
        cashValue: DEFAULT_REDEEM_CASH_VALUE,
        pointsCost: DEFAULT_REDEEM_POINTS_COST,
      },
    },
    actionRewards: {
      rebookingEnabled: true,
      rebookingPoints: 50,
      visitsEnabled: true,
      visitsPoints: 50,
      visitsInterval: 3,
      firstAppointmentEnabled: false,
      firstAppointmentPoints: 50,
    },
  };
}

export const PRESET_LABELS: Record<LoyaltyPreset, string> = {
  both: "Standard",
  retail: "Retail Only",
  service: "Service Only",
};

export function presetDescription(
  preset: LoyaltyPreset,
  servicesRate: number | string = EARN_POINTS,
  productsRate: number | string = EARN_POINTS,
): string {
  const services = toNumber(servicesRate, EARN_POINTS);
  const products = toNumber(productsRate, EARN_POINTS);
  const dollars = formatCurrency(EARN_PER_AMOUNT);
  switch (preset) {
    case "retail":
      return `Earn ${products} points for every ${dollars} spent on products only`;
    case "service":
      return `Earn ${services} points for every ${dollars} spent on services only`;
    default:
      return services === products
        ? `Earn ${services} points for every ${dollars} spent on services or products`
        : `Earn ${services} points per ${dollars} on services and ${products} points per ${dollars} on products`;
  }
}
